import { Button, ButtonIcon, ButtonSize, ButtonVariant, Chip, ChipSize, ChipVariant, IconVariants, Modal, Shape } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { useState } from "react";

import Trans from "@/components/Trans/Trans";

import { LABEL } from "../voices/types";
import { ChatModalRateSpeechProps, ListItems, RateSpeechActions } from "./type";

export const RateSpeechModal = ({ listItems, open, onOpenChange, actionHandler, voiceRateLoading }: ChatModalRateSpeechProps) => {
  const { t } = useTranslation();
  const [selectedFeedback, setSelectedFeedback] = useState<LABEL | null>(null);
  const [selectedAction, setSelectedAction] = useState<RateSpeechActions | null>(null);

  const disableSave = !selectedAction || (selectedAction === RateSpeechActions.DISLIKE && !selectedFeedback) || voiceRateLoading;

  const closeModal = () => {
    onOpenChange?.(false);
  };

  const onOpenChangeHandler = () => {
    if (voiceRateLoading) {
      return;
    }
    closeModal();
  };

  const handleRatingAction = (action: RateSpeechActions) => {
    setSelectedAction(action);
    if (action !== selectedAction) {
      setSelectedFeedback(null);
    }
    actionHandler(action);
  };

  const confirmRatingAction = () => {
    if (selectedAction === RateSpeechActions.DISLIKE) {
      if (!selectedFeedback) {
        return;
      }
      actionHandler(RateSpeechActions.CONFIRM, selectedFeedback);
    } else {
      actionHandler(RateSpeechActions.CONFIRM);
    }
  };

  return (
    <Modal
      open={open}
      title={t("chat:modal.rateVoiceQuality.title")}
      description={t("chat:modal.rateVoiceQuality.description")}
      onOpenChange={onOpenChangeHandler}
    >
      <div className="flex justify-between gap-2">
        <ButtonIcon
          className={`flex-1 hover:text-white border-1 border-solid border-gray-9 hover:bg-blue-9 hover:border-blue-9 active:bg-blue-9 active:border-blue-9 ${
            selectedAction === RateSpeechActions.LIKE ? `bg-blue-9 border-blue-9` : "border-gray-9"
          }`}
          icon={IconVariants.LIKE}
          size={ButtonSize.LARGE}
          variant={selectedAction === RateSpeechActions.LIKE ? ButtonVariant.CONTAINED : ButtonVariant.TRANSPARENT}
          shape={Shape.SQUARE}
          onClick={() => handleRatingAction(RateSpeechActions.LIKE)}
          aria-label="rate-speech-like"
        />
        <ButtonIcon
          className={`flex-1  hover:text-white border-1 border-solid border-gray-9 hover:bg-red-9 hover:border-red-9 active:bg-red-9 active:border-red-9  ${
            selectedAction === RateSpeechActions.DISLIKE ? `bg-red-9 border-red-9` : "border-gray-9"
          }`}
          icon={IconVariants.DISLIKE}
          size={ButtonSize.LARGE}
          variant={selectedAction === RateSpeechActions.DISLIKE ? ButtonVariant.CONTAINED : ButtonVariant.TRANSPARENT}
          shape={Shape.SQUARE}
          onClick={() => handleRatingAction(RateSpeechActions.DISLIKE)}
          aria-label="rate-speech-dislike"
        />
        <ButtonIcon
          className={`flex-1  hover:text-white border-1 border-solid border-gray-9 hover:bg-purple-9 hover:border-purple-9 active:bg-purple-9 active:border-purple-9 ${
            selectedAction === RateSpeechActions.FAVORITE ? `bg-purple-9 border-purple-9` : "border-gray-9"
          }`}
          icon={IconVariants.FAVORITE}
          size={ButtonSize.LARGE}
          variant={selectedAction === RateSpeechActions.FAVORITE ? ButtonVariant.CONTAINED : ButtonVariant.TRANSPARENT}
          shape={Shape.SQUARE}
          onClick={() => handleRatingAction(RateSpeechActions.FAVORITE)}
          aria-label="rate-speech-favorite"
        />
      </div>
      {!!listItems?.length && (
        <div className="flex gap-2 flex-wrap justify-center">
          {listItems.map((option: ListItems, index) => (
            <Chip
              key={index}
              variant={selectedFeedback === option?.subLabel ? ChipVariant.DEFAULT : ChipVariant.OUTLINED}
              className={selectedFeedback === option?.subLabel ? "!border !border-solid !border-gray-6" : ""}
              onClick={() => setSelectedFeedback(option.subLabel)}
              size={ChipSize.SMALL}
              aria-label={option.label}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      )}

      <div className="flex gap-[6px] justify-end">
        <Button className="px-lg" size={ButtonSize.LARGE} onClick={closeModal} variant={ButtonVariant.TRANSPARENT} aria-label="cancel">
          <Trans i18nKey="common:cancel" />
        </Button>
        <Button
          className="px-lg text-white hover:border-blue-9 active:border-blue-5"
          disabled={disableSave}
          size={ButtonSize.LARGE}
          onClick={() => confirmRatingAction()}
          variant={ButtonVariant.CONTAINED}
          loading={voiceRateLoading}
          aria-label="done"
        >
          <Trans i18nKey="common:done" />
        </Button>
      </div>
    </Modal>
  );
};
